import { request } from "../../../../../services/api"
import type { BaseApiResponse } from "../../../../../types/api.ts"
import type {
  AudioAssetView,
  CreateShareLinkData,
  EditionView,
  EducationApiError,
  LeadEducationView,
  OperationView,
  RequestMoreData,
  SaveProgressData,
  SendFeedbackData,
  ShareLinkView,
  StartGenerationData,
  StartGenerationResponse
} from "../types/education.types.ts"

// `apps.project_education` (ezbuild_back), mounted at `api/project-education/`.
// GET responses are the bare shape; POST/PUT success responses are enveloped
// (`response.details.data`) — callers unwrap, per this repo's convention (see
// e.g. `Project/api/project-documents.services.ts`). Error responses here are
// never enveloped (`EducationApiError` at the top level), unlike most of this
// app's error responses.
const BASE_URL = "/project-education"

const PATHS = {
  lead: (leadId: string) => `${BASE_URL}/leads/${leadId}/`,
  generations: (leadId: string) => `${BASE_URL}/leads/${leadId}/generations/`,
  operation: (packageId: string) => `${BASE_URL}/operations/${packageId}/`,
  trackEditions: (trackId: string) => `${BASE_URL}/tracks/${trackId}/editions/`,
  editionRetry: (id: string) => `${BASE_URL}/editions/${id}/retry/`,
  editionPublish: (id: string) => `${BASE_URL}/editions/${id}/publish/`,
  editionApprove: (id: string) => `${BASE_URL}/editions/${id}/approve/`,
  editionAudio: (id: string) => `${BASE_URL}/editions/${id}/audio/`,
  editionAudioRetry: (id: string) => `${BASE_URL}/editions/${id}/audio/retry/`,
  editionViewed: (id: string) => `${BASE_URL}/editions/${id}/viewed/`,
  editionProgress: (id: string) => `${BASE_URL}/editions/${id}/progress/`,
  editionFeedback: (id: string) => `${BASE_URL}/editions/${id}/feedback/`,
  leadShareLinks: (leadId: string) => `${BASE_URL}/leads/${leadId}/share-links/`,
  shareLinkRevoke: (id: string) => `${BASE_URL}/share-links/${id}/revoke/`,
  shareLinkExtend: (id: string) => `${BASE_URL}/share-links/${id}/extend/`
}

const idempotencyHeader = (idempotencyKey: string) => ({
  headers: { "Idempotency-Key": idempotencyKey }
})

// GET /project-education/leads/:leadId/ — not enveloped.
export const getLeadEducation = async (leadId: string) => {
  return request.api.get<LeadEducationView, EducationApiError>(PATHS.lead(leadId))
}

// POST /project-education/leads/:leadId/generations/ — 202, enveloped.
export const startGeneration = async (
  leadId: string,
  body: StartGenerationData,
  idempotencyKey: string
) => {
  return request.api.post<
    BaseApiResponse<StartGenerationResponse>,
    EducationApiError
  >(PATHS.generations(leadId), body, idempotencyHeader(idempotencyKey))
}

// GET /project-education/operations/:packageId/ — not enveloped.
export const getOperation = async (packageId: string) => {
  return request.api.get<OperationView, EducationApiError>(PATHS.operation(packageId))
}

// POST /project-education/tracks/:trackId/editions/ — 202, enveloped.
export const requestMore = async (
  trackId: string,
  body: RequestMoreData,
  idempotencyKey: string
) => {
  return request.api.post<BaseApiResponse<EditionView>, EducationApiError>(
    PATHS.trackEditions(trackId),
    body,
    idempotencyHeader(idempotencyKey)
  )
}

// POST /project-education/editions/:id/retry/ — 202, enveloped.
export const retryEdition = async (id: string) => {
  return request.api.post<BaseApiResponse<EditionView>, EducationApiError>(
    PATHS.editionRetry(id)
  )
}

// POST /project-education/editions/:id/publish/ — 200, enveloped.
export const publishEdition = async (id: string, expectedUpdated?: string) => {
  return request.api.post<BaseApiResponse<EditionView>, EducationApiError>(
    PATHS.editionPublish(id),
    expectedUpdated === undefined ? {} : { expected_updated: expectedUpdated }
  )
}

// POST /project-education/editions/:id/approve/ — 200, enveloped.
export const approveEdition = async (id: string) => {
  return request.api.post<BaseApiResponse<EditionView>, EducationApiError>(
    PATHS.editionApprove(id)
  )
}

// POST /project-education/editions/:id/audio/ — 200 once ready, else 202
// with only `status`; enveloped either way.
export const getAudio = async (id: string, voiceId?: string) => {
  return request.api.post<BaseApiResponse<AudioAssetView>, EducationApiError>(
    PATHS.editionAudio(id),
    voiceId ? { voice_id: voiceId } : {}
  )
}

// POST /project-education/editions/:id/audio/retry/ — 202, no body.
export const retryAudio = async (id: string) => {
  return request.api.post<BaseApiResponse<null>, EducationApiError>(
    PATHS.editionAudioRetry(id)
  )
}

// POST /project-education/editions/:id/viewed/ — 204, enveloped like every
// other POST here; records that the current internal user viewed the
// edition. Idempotent server-side (`record_view` sets `first_viewed_at` once
// and refreshes `last_viewed_at` every call), so callers don't need to guard
// against sending it more than once — this file's own caller (
// `EducationTrackView`) still does, to avoid a network call per rerender.
export const markEditionViewed = async (id: string) => {
  return request.api.post<BaseApiResponse<null>, EducationApiError>(
    PATHS.editionViewed(id)
  )
}

// PUT /project-education/editions/:id/progress/ — 204, still carries an
// enveloped `{data: null}` body in prod (PUT-204 is not the DELETE-204
// special case) — see backend-wire-notes.md.
export const saveProgress = async (id: string, body: SaveProgressData) => {
  return request.api.put<BaseApiResponse<null>, EducationApiError>(
    PATHS.editionProgress(id),
    body
  )
}

// POST /project-education/editions/:id/feedback/ — 201, no body, enveloped.
export const sendFeedback = async (id: string, body: SendFeedbackData) => {
  return request.api.post<BaseApiResponse<null>, EducationApiError>(
    PATHS.editionFeedback(id),
    body
  )
}

// POST /project-education/leads/:leadId/share-links/ — 201, enveloped.
export const createShareLink = async (leadId: string, body: CreateShareLinkData) => {
  return request.api.post<
    BaseApiResponse<{ link: ShareLinkView; url: string }>,
    EducationApiError
  >(PATHS.leadShareLinks(leadId), body)
}

// POST /project-education/share-links/:id/revoke/ — 204, enveloped.
export const revokeShareLink = async (id: string) => {
  return request.api.post<BaseApiResponse<null>, EducationApiError>(
    PATHS.shareLinkRevoke(id)
  )
}

// POST /project-education/share-links/:id/extend/ — 200, enveloped.
export const extendShareLink = async (id: string, expiresAt: string) => {
  return request.api.post<BaseApiResponse<ShareLinkView>, EducationApiError>(
    PATHS.shareLinkExtend(id),
    { expires_at: expiresAt }
  )
}
